import React, { useEffect, useImperativeHandle, useRef, forwardRef } from "react";

const SignaturePad = forwardRef(function SignaturePad({ height = 160, onChange }, ref) {
  const canvasRef = useRef(null);
  const ctxRef = useRef(null);
  const drawing = useRef(false);
  const hasStrokes = useRef(false);
  const last = useRef({ x: 0, y: 0 });

  const resize = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = height * dpr;
    canvas.style.height = `${height}px`;
    const ctx = canvas.getContext("2d");
    ctx.scale(dpr, dpr);
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#141414";
    ctxRef.current = ctx;
  };

  useEffect(() => {
    resize();
    const on = () => resize();
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const point = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: t.clientX - rect.left, y: t.clientY - rect.top };
  };

  const start = (e) => {
    e.preventDefault();
    drawing.current = true;
    last.current = point(e);
  };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const p = point(e);
    const ctx = ctxRef.current;
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
    hasStrokes.current = true;
  };
  const end = () => {
    if (drawing.current && onChange) onChange(canvasRef.current.toDataURL("image/png"));
    drawing.current = false;
  };

  useImperativeHandle(ref, () => ({
    clear: () => {
      const c = canvasRef.current;
      if (c && ctxRef.current) ctxRef.current.clearRect(0, 0, c.width, c.height);
      hasStrokes.current = false;
      if (onChange) onChange("");
    },
    isEmpty: () => !hasStrokes.current,
    toDataURL: () => (hasStrokes.current ? canvasRef.current.toDataURL("image/png") : ""),
  }));

  return (
    <canvas
      ref={canvasRef}
      onMouseDown={start}
      onMouseMove={move}
      onMouseUp={end}
      onMouseLeave={end}
      onTouchStart={start}
      onTouchMove={move}
      onTouchEnd={end}
      className="w-full bg-white border-b-2 hairline cursor-crosshair touch-none"
      style={{ height }}
      data-testid="signature-canvas"
    />
  );
});

export default SignaturePad;
